import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { IpAddressLookup } from '@/components/IpAddressLookup'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/reports')({
  component: ReportsPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type TargetType = 'comment' | 'danmaku'
type ReportStatus = 'pending' | 'resolved' | 'dismissed' | 'all'
type ContentAction = 'dismiss' | 'hide' | 'shadow' | 'delete'

interface ReportRow {
  target_type: TargetType
  target_id: number
  target_content: string | null
  target_status: string | null
  target_deleted: boolean
  target_author_id: string | null
  target_author_username: string | null
  target_author_display: string
  anime_id: number | null
  anime_title: string | null
  episode_id: number | null
  episode_number: number | null
  total_count: number
  pending_count: number
  resolved_count: number
  dismissed_count: number
  reasons: Record<string, number>
  earliest_at: string
  latest_at: string
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<ReportRow>
  error?: string
}

interface ReporterDetail {
  id: number
  reporter_id: string | null
  reporter_username: string | null
  reporter_display: string
  reason: string
  status: string
  created_at: string
  resolved_at: string | null
  resolved_by: string | null
}

interface TargetDetail {
  ok: boolean
  target?: {
    target_type: TargetType
    id: number
    content?: string
    status?: string
    deleted?: boolean
    created_at?: string
    moderated_at?: string | null
    moderator_kind?: string | null
    report_count?: number
    ip_address?: string | null
    author?: {
      id: string | null
      username: string | null
      display_name: string | null
      legacy_name?: string | null
    }
    context?: {
      anime_id?: number | null
      anime_title?: string | null
      episode_id?: number | null
      episode_number?: number | null
    }
    parent_id?: number | null
    v_time?: number | null
    mode?: string | null
  }
  reports?: Array<ReporterDetail>
  error?: string
}

/* ── i18n ───────────────────────────────────────────────────── */

const TARGET_TYPE_LABELS: Record<string, string> = {
  comment: '评论',
  danmaku: '弹幕',
}

const REASON_LABELS: Record<string, string> = {
  spam: '垃圾信息',
  sexual: '色情',
  insult: '侮辱',
  flamebait: '挑衅',
  harassment: '骚扰',
  political: '政治',
  illegal: '违法',
  other: '其他',
  vent: '单纯急了',
}

const CONTENT_STATUS_LABELS: Record<string, string> = {
  visible: '可见',
  hidden: '隐藏',
  shadow: '影子',
  deleted: '已删除',
  pending: '待审',
  spam: '垃圾',
}

const REPORT_STATUS_LABELS: Record<string, string> = {
  pending: '待处理',
  resolved: '已处理',
  dismissed: '已驳回',
}

function translate(
  map: Record<string, string>,
  key: string | null | undefined,
): string {
  if (!key) return '—'
  return map[key] ?? key
}

/* ── 动作元数据 ─────────────────────────────────────────────── */

interface ActionMeta {
  value: ContentAction
  label: string
  desc: string
  danger?: boolean
  requireNote: boolean // delete 必填
  confirmMessage: string
}

const ACTIONS: Array<ActionMeta> = [
  {
    value: 'dismiss',
    label: '驳回',
    desc: '内容保留可见，所有 pending 举报标为已驳回',
    requireNote: false,
    confirmMessage:
      '将该 target 下的所有待处理举报标为「已驳回」，内容保留可见。继续？',
  },
  {
    value: 'hide',
    label: '隐藏',
    desc: '对 C 端全员隐藏（含作者本人）',
    requireNote: false,
    confirmMessage:
      '将内容设为「隐藏」，C 端全员（含作者本人）都看不到。继续？',
  },
  {
    value: 'shadow',
    label: '影子',
    desc: '仅作者本人可见，他人完全看不到（shadow ban，调教恶意用户用）',
    requireNote: false,
    confirmMessage:
      '将内容设为「影子」，作者本人仍可见（以为发出去了），但他人完全看不到。继续？',
  },
  {
    value: 'delete',
    label: '删除',
    desc: '硬删，触发器级联清理 reports',
    danger: true,
    requireNote: true,
    confirmMessage: '硬删该内容（不可恢复），触发器级联清掉所有举报。继续？',
  },
]

/* ── helpers ────────────────────────────────────────────────── */

const PAGE_SIZE = 50

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function targetTypeColor(t: string) {
  return t === 'danmaku' ? 'purple' : 'blue'
}

function reportStatusColor(s: string) {
  switch (s) {
    case 'pending':
      return 'warning'
    case 'resolved':
      return 'success'
    case 'dismissed':
      return 'default'
    default:
      return 'default'
  }
}

function contentStatusColor(s: string | null | undefined) {
  switch (s) {
    case 'visible':
      return 'success'
    case 'hidden':
      return 'warning'
    case 'shadow':
      return 'magenta'
    case 'deleted':
      return 'default'
    case 'pending':
    case 'spam':
      return 'warning'
    default:
      return 'default'
  }
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function ReportsPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()

  const [tab, setTab] = useState<ReportStatus>('pending')
  const [page, setPage] = useState(1)
  const [targetType, setTargetType] = useState<TargetType | undefined>()
  const [reason, setReason] = useState<string | undefined>()
  const [includeVent, setIncludeVent] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim(), 300)

  const [detail, setDetail] = useState<{
    target_type: TargetType
    target_id: number
  } | null>(null)

  // tab / filter 变了回首页
  useEffect(() => {
    setPage(1)
  }, [tab, targetType, reason, includeVent, debouncedSearch])

  const listQuery = useQuery({
    queryKey: [
      'reports',
      {
        tab,
        page,
        targetType,
        reason,
        includeVent,
        search: debouncedSearch,
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_reports', {
          p_status: tab,
          p_target_type: targetType ?? null,
          p_reason: reason ?? null,
          p_include_vent: includeVent,
          p_search: debouncedSearch || null,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return { rows: r.rows ?? [], total: r.total ?? 0 }
    },
  })

  // 单条驳回
  const dismissOneMutation = useMutation({
    mutationFn: async (params: { reportId: number; note?: string }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('dismiss_one_report', {
          p_report_id: params.reportId,
          p_note: params.note ?? null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: () => {
      message.success('已驳回该条举报')
      qc.invalidateQueries({ queryKey: ['reports'] })
      qc.invalidateQueries({ queryKey: ['report_detail'] })
    },
    onError: (e: Error) => message.error(e.message || '驳回失败'),
  })

  const columns: ColumnsType<ReportRow> = useMemo(
    () => [
      {
        title: '类型',
        width: 90,
        render: (_: unknown, row) => (
          <Tag color={targetTypeColor(row.target_type)}>
            {translate(TARGET_TYPE_LABELS, row.target_type)}
          </Tag>
        ),
      },
      {
        title: '内容',
        render: (_: unknown, row) => (
          <Space size={2} direction="vertical" style={{ rowGap: 0 }}>
            {row.target_deleted ? (
              <Typography.Text type="secondary" italic>
                （内容已删除）
              </Typography.Text>
            ) : (
              <Tooltip title={row.target_content} mouseEnterDelay={0.3}>
                <Typography.Paragraph
                  ellipsis={{ rows: 2 }}
                  style={{ marginBottom: 0, maxWidth: 480 }}
                >
                  {row.target_content || '—'}
                </Typography.Paragraph>
              </Tooltip>
            )}
            <Space size={6} wrap>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                ID {row.target_id}
              </Typography.Text>
              {row.target_status ? (
                <Tag
                  bordered={false}
                  color={contentStatusColor(row.target_status)}
                  style={{ fontSize: 11, marginRight: 0 }}
                >
                  {translate(CONTENT_STATUS_LABELS, row.target_status)}
                </Tag>
              ) : null}
              {row.anime_title ? (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  · {row.anime_title}
                  {row.episode_number != null
                    ? `（第 ${row.episode_number} 集）`
                    : ''}
                </Typography.Text>
              ) : null}
            </Space>
          </Space>
        ),
      },
      {
        title: '作者',
        width: 140,
        render: (_: unknown, row) =>
          row.target_author_id ? (
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 13 }}>
                {row.target_author_display}
              </Typography.Text>
              {row.target_author_username ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  copyable={{ text: row.target_author_id }}
                >
                  @{row.target_author_username}
                </Typography.Text>
              ) : null}
            </Space>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              已注销
            </Typography.Text>
          ),
      },
      {
        title: '举报量',
        width: 100,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2}>
            <Badge
              count={row.pending_count}
              showZero={false}
              overflowCount={999}
              style={{ backgroundColor: '#faad14' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              共 {row.total_count}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '原因分布',
        width: 220,
        render: (_: unknown, row) => (
          <Space size={4} wrap>
            {Object.entries(row.reasons)
              .sort((a, b) => b[1] - a[1])
              .map(([reasonKey, count]) => (
                <Tag
                  key={reasonKey}
                  color={reasonKey === 'vent' ? 'default' : 'volcano'}
                  style={{ marginRight: 0 }}
                >
                  {translate(REASON_LABELS, reasonKey)} ×{count}
                </Tag>
              ))}
          </Space>
        ),
      },
      {
        title: '最早 / 最新',
        width: 150,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCST(row.earliest_at, 'MM-DD HH:mm')}
            </Typography.Text>
            <Typography.Text
              style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCST(row.latest_at, 'MM-DD HH:mm')}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '操作',
        width: 90,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setDetail({
                target_type: row.target_type,
                target_id: row.target_id,
              })
            }}
          >
            受理
          </Button>
        ),
      },
    ],
    [],
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0

  return (
    <>
      <Card
        title="举报受理"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => listQuery.refetch()}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div className="admin-scroll-rail moderation-tab-rail">
            <Segmented<ReportStatus>
              value={tab}
              onChange={(v) => setTab(v)}
              options={[
                { label: '待处理', value: 'pending' },
                { label: '已处理', value: 'resolved' },
                { label: '已驳回', value: 'dismissed' },
                { label: '全部', value: 'all' },
              ]}
            />
          </div>

          <AdminFilterBar
            primary={
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="搜内容片段"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 240 }}
              />
            }
            activeCount={
              (targetType ? 1 : 0) + (reason ? 1 : 0) + (includeVent ? 1 : 0)
            }
          >
            <Select
              allowClear
              placeholder="类型"
              style={{ width: 130 }}
              value={targetType}
              onClear={() => setTargetType(undefined)}
              onChange={(v) => setTargetType(v)}
              options={Object.entries(TARGET_TYPE_LABELS).map(
                ([value, label]) => ({
                  value,
                  label,
                }),
              )}
            />
            <Select
              allowClear
              showSearch
              placeholder="原因"
              style={{ width: 160 }}
              value={reason}
              onClear={() => setReason(undefined)}
              onChange={(v) => setReason(v)}
              options={Object.entries(REASON_LABELS).map(([value, label]) => ({
                value,
                label: `${label} · ${value}`,
              }))}
              optionFilterProp="label"
            />
            <Space size={6} className="reports-vent-toggle">
              <span style={{ fontSize: 13 }}>单纯急了</span>
              <Switch
                checked={includeVent}
                onChange={setIncludeVent}
                size="small"
              />
            </Space>
          </AdminFilterBar>

          <div className="admin-table-wrap">
            <Table<ReportRow>
              rowKey={(r) => `${r.target_type}:${r.target_id}`}
              size="middle"
              loading={listQuery.isLoading}
              columns={columns}
              dataSource={rows}
              expandable={{
                expandedRowRender: (row) => (
                  <ExpandedReporters
                    targetType={row.target_type}
                    targetId={row.target_id}
                    onDismissOne={(reportId, note) =>
                      dismissOneMutation.mutate({ reportId, note })
                    }
                    dismissing={dismissOneMutation.isPending}
                  />
                ),
              }}
              scroll={{ x: 1100 }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                showTotal: (totalRows) => `共 ${totalRows} 条`,
                onChange: (p) => setPage(p),
              }}
            />
          </div>

          <AdminMobileList
            rows={rows}
            rowKey={(row) => `${row.target_type}:${row.target_id}`}
            loading={listQuery.isLoading || listQuery.isFetching}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            renderItem={(row) => (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                onClick={() =>
                  setDetail({
                    target_type: row.target_type,
                    target_id: row.target_id,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setDetail({
                      target_type: row.target_type,
                      target_id: row.target_id,
                    })
                  }
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">
                    <Typography.Paragraph ellipsis={{ rows: 2 }}>
                      {row.target_deleted
                        ? '（内容已删除）'
                        : row.target_content || '—'}
                    </Typography.Paragraph>
                  </div>
                  <Tag color={targetTypeColor(row.target_type)}>
                    {translate(TARGET_TYPE_LABELS, row.target_type)}
                  </Tag>
                </div>
                <div className="admin-mobile-record__badges">
                  {row.target_status ? (
                    <Tag color={contentStatusColor(row.target_status)}>
                      {translate(CONTENT_STATUS_LABELS, row.target_status)}
                    </Tag>
                  ) : null}
                  {Object.entries(row.reasons)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 4)
                    .map(([reasonKey, count]) => (
                      <Tag
                        key={reasonKey}
                        color={reasonKey === 'vent' ? 'default' : 'volcano'}
                      >
                        {translate(REASON_LABELS, reasonKey)} ×{count}
                      </Tag>
                    ))}
                </div>
                <div className="admin-mobile-record__meta">
                  <span>ID {row.target_id}</span>
                  <span>作者 {row.target_author_display || '已注销'}</span>
                  {row.anime_title ? (
                    <span>
                      {row.anime_title}
                      {row.episode_number != null
                        ? ` · 第 ${row.episode_number} 集`
                        : ''}
                    </span>
                  ) : null}
                  <span>最新 {formatCST(row.latest_at, 'MM-DD HH:mm')}</span>
                </div>
                <AdminRecordActions
                  leading={
                    <span>
                      待处理{' '}
                      <Typography.Text
                        strong
                        type={row.pending_count > 0 ? 'warning' : 'secondary'}
                      >
                        {row.pending_count}
                      </Typography.Text>{' '}
                      · 共 {row.total_count}
                    </span>
                  }
                  actions={[
                    {
                      key: 'resolve',
                      label: row.pending_count > 0 ? '受理' : '查看',
                      onClick: () =>
                        setDetail({
                          target_type: row.target_type,
                          target_id: row.target_id,
                        }),
                    },
                  ]}
                />
              </article>
            )}
          />
        </Space>
      </Card>

      <ResolveDrawer
        target={detail}
        onClose={() => setDetail(null)}
        onDismissOne={(reportId) => dismissOneMutation.mutate({ reportId })}
        dismissing={dismissOneMutation.isPending}
      />
    </>
  )
}

/* ── 展开行：该 target 的所有举报人 + 单条驳回 ───────────────── */

function ExpandedReporters({
  targetType,
  targetId,
  onDismissOne,
  dismissing,
}: {
  targetType: TargetType
  targetId: number
  onDismissOne: (reportId: number, note?: string) => void
  dismissing: boolean
}) {
  const detailQuery = useQuery({
    queryKey: ['report_detail', targetType, targetId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_report_target', {
          p_target_type: targetType,
          p_target_id: targetId,
        })
      if (error) throw error
      const r = data as TargetDetail
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
  })

  const reports = detailQuery.data?.reports ?? []

  const cols: ColumnsType<ReporterDetail> = [
    {
      title: '举报人',
      width: 200,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <Typography.Text style={{ fontSize: 13 }}>
            {r.reporter_display}
          </Typography.Text>
          {r.reporter_username ? (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              @{r.reporter_username}
            </Typography.Text>
          ) : r.reporter_id ? (
            <Typography.Text
              type="secondary"
              style={{
                fontSize: 11,
                fontFamily: 'var(--ant-font-family-code, monospace)',
              }}
              copyable={{ text: r.reporter_id }}
            >
              {r.reporter_id.slice(0, 8)}…
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              已注销
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: '原因',
      width: 140,
      render: (_: unknown, r) => (
        <Tag color={r.reason === 'vent' ? 'default' : 'volcano'}>
          {translate(REASON_LABELS, r.reason)}
        </Tag>
      ),
    },
    {
      title: '时间',
      width: 160,
      render: (_: unknown, r) => (
        <Typography.Text
          style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatCST(r.created_at, 'YYYY-MM-DD HH:mm')}
        </Typography.Text>
      ),
    },
    {
      title: '状态',
      width: 100,
      render: (_: unknown, r) => (
        <Tag color={reportStatusColor(r.status)}>
          {translate(REPORT_STATUS_LABELS, r.status)}
        </Tag>
      ),
    },
    {
      title: '',
      render: (_: unknown, r) =>
        r.status === 'pending' ? (
          <Button
            size="small"
            type="link"
            loading={dismissing}
            onClick={() => onDismissOne(r.id)}
          >
            驳回这条
          </Button>
        ) : null,
    },
  ]

  return (
    <Table<ReporterDetail>
      rowKey="id"
      size="small"
      loading={detailQuery.isLoading}
      columns={cols}
      dataSource={reports}
      pagination={false}
      style={{ margin: '0 8px' }}
    />
  )
}

/* ── 受理抽屉：完整 target + 处理面板 ────────────────────────── */

function ResolveDrawer({
  target,
  onClose,
  onDismissOne,
  dismissing,
}: {
  target: { target_type: TargetType; target_id: number } | null
  onClose: () => void
  /** 单条驳回：桌面在表格展开行里做，手机没有展开行，放到抽屉的举报人列表里 */
  onDismissOne: (reportId: number) => void
  dismissing: boolean
}) {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [note, setNote] = useState('')
  const [pendingAction, setPendingAction] = useState<ContentAction | null>(null)

  useEffect(() => {
    setNote('')
    setPendingAction(null)
  }, [target?.target_type, target?.target_id])

  const detailQuery = useQuery({
    queryKey: ['report_detail', target?.target_type, target?.target_id],
    enabled: !!target,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_report_target', {
          p_target_type: target!.target_type,
          p_target_id: target!.target_id,
        })
      if (error) throw error
      const r = data as TargetDetail
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
  })

  const resolveMutation = useMutation({
    mutationFn: async (params: { action: ContentAction; note: string }) => {
      if (!target) throw new Error('no_target')

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('resolve_reports', {
          p_target_type: target.target_type,
          p_target_id: target.target_id,
          p_action: params.action,
          p_note: params.note || null,
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        processed_count?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      message.success(`已处理 ${r.processed_count ?? 0} 条举报`)
      qc.invalidateQueries({ queryKey: ['reports'] })
      qc.invalidateQueries({ queryKey: ['report_detail'] })
      onClose()
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error('删除操作必须填写不少于 5 字的备注')
      } else {
        message.error(e.message || '处理失败')
      }
    },
  })

  const tgt = detailQuery.data?.target
  const reports = detailQuery.data?.reports ?? []
  const pendingCount = reports.filter((r) => r.status === 'pending').length

  const handleAction = (a: ActionMeta) => {
    if (a.requireNote && note.trim().length < 5) {
      message.warning('此动作必须填写不少于 5 字的备注')
      return
    }
    setPendingAction(a.value)
    modal.confirm({
      title: `确认「${a.label}」？`,
      content: a.confirmMessage,
      okText: '执行',
      cancelText: '取消',
      okButtonProps: { danger: a.danger },
      onOk: () =>
        resolveMutation.mutate({ action: a.value, note: note.trim() }),
      onCancel: () => setPendingAction(null),
      afterClose: () => setPendingAction(null),
    })
  }

  return (
    <Drawer
      open={!!target}
      onClose={onClose}
      width={isMobile ? '100%' : 720}
      className="reports-resolve-drawer"
      title={
        target
          ? `${translate(TARGET_TYPE_LABELS, target.target_type)} #${target.target_id} · 受理`
          : ''
      }
      destroyOnHidden
    >
      {!target ? null : detailQuery.isLoading ? (
        <Typography.Text type="secondary">加载中…</Typography.Text>
      ) : tgt ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {tgt.deleted ? (
            <Alert
              type="warning"
              message="该内容已被硬删除（可能由级联触发器清掉）"
            />
          ) : null}

          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              {
                label: '类型',
                children: (
                  <Tag color={targetTypeColor(tgt.target_type)}>
                    {translate(TARGET_TYPE_LABELS, tgt.target_type)}
                  </Tag>
                ),
              },
              {
                label: '内容',
                children: tgt.content ? (
                  <Typography.Paragraph
                    style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                  >
                    {tgt.content}
                  </Typography.Paragraph>
                ) : (
                  <Typography.Text type="secondary" italic>
                    {tgt.deleted ? '（已删除）' : '—'}
                  </Typography.Text>
                ),
              },
              {
                label: '内容状态',
                children: tgt.status ? (
                  <Tag color={contentStatusColor(tgt.status)}>
                    {translate(CONTENT_STATUS_LABELS, tgt.status)} ·{' '}
                    {tgt.status}
                  </Tag>
                ) : (
                  '—'
                ),
              },
              {
                label: '作者',
                children: tgt.author?.id ? (
                  <Space direction="vertical" size={0}>
                    <span>
                      {tgt.author.display_name ||
                        tgt.author.username ||
                        tgt.author.legacy_name ||
                        '—'}
                    </span>
                    {tgt.author.username ? (
                      <Typography.Text type="secondary">
                        @{tgt.author.username}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text
                      type="secondary"
                      copyable
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {tgt.author.id}
                    </Typography.Text>
                  </Space>
                ) : tgt.author?.legacy_name ? (
                  <Typography.Text>{tgt.author.legacy_name}</Typography.Text>
                ) : (
                  <Typography.Text type="secondary">已注销</Typography.Text>
                ),
              },
              {
                label: '上下文',
                children: tgt.context?.anime_title ? (
                  <>
                    《{tgt.context.anime_title}》
                    {tgt.context.episode_number != null
                      ? `· 第 ${tgt.context.episode_number} 集`
                      : ''}
                  </>
                ) : (
                  '—'
                ),
              },
              {
                label: '发布时间',
                children: formatCST(tgt.created_at),
              },
              {
                label: 'IP',
                children: <IpAddressLookup ip={tgt.ip_address} />,
              },
              {
                label: '历史审核',
                children: tgt.moderated_at ? (
                  <>
                    {formatCST(tgt.moderated_at, 'YYYY-MM-DD HH:mm')} ·{' '}
                    {tgt.moderator_kind ?? '?'}
                  </>
                ) : (
                  <Typography.Text type="secondary">未审过</Typography.Text>
                ),
              },
            ]}
          />

          <div>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              处理面板
            </Typography.Title>
            <Alert
              type="info"
              showIcon
              message={`该内容共 ${reports.length} 条举报，其中待处理 ${pendingCount} 条`}
              style={{ marginBottom: 12 }}
            />
            <Input.TextArea
              placeholder="备注（删除必填 ≥5 字；其他可选，会写入审计日志）"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
              showCount
              style={{ marginBottom: 12 }}
            />
            <Space wrap className="reports-action-grid">
              {ACTIONS.map((a) => (
                <Tooltip key={a.value} title={a.desc} mouseEnterDelay={0.4}>
                  <Button
                    danger={a.danger}
                    loading={
                      resolveMutation.isPending && pendingAction === a.value
                    }
                    disabled={
                      resolveMutation.isPending && pendingAction !== a.value
                    }
                    onClick={() => handleAction(a)}
                  >
                    {a.label}
                  </Button>
                </Tooltip>
              ))}
            </Space>
          </div>

          <div>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              举报人 ({reports.length})
            </Typography.Title>
            {isMobile ? (
              <div className="reports-reporter-list">
                {reports.map((r) => (
                  <div key={r.id} className="reports-reporter-item">
                    <div className="reports-reporter-item__main">
                      <span className="reports-reporter-item__name">
                        {r.reporter_display}
                        {r.reporter_username ? (
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12, marginLeft: 6 }}
                          >
                            @{r.reporter_username}
                          </Typography.Text>
                        ) : null}
                      </span>
                      <Space size={4} wrap>
                        <Tag
                          color={r.reason === 'vent' ? 'default' : 'volcano'}
                          style={{ marginRight: 0 }}
                        >
                          {translate(REASON_LABELS, r.reason)}
                        </Tag>
                        <Tag
                          color={reportStatusColor(r.status)}
                          style={{ marginRight: 0 }}
                        >
                          {translate(REPORT_STATUS_LABELS, r.status)}
                        </Tag>
                        <Typography.Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatCST(r.created_at, 'MM-DD HH:mm')}
                        </Typography.Text>
                      </Space>
                    </div>
                    {r.status === 'pending' ? (
                      <Button
                        size="small"
                        loading={dismissing}
                        onClick={() =>
                          modal.confirm({
                            title: '驳回这条举报？',
                            content:
                              '仅驳回该举报人的这一条，内容与其他举报不受影响。',
                            okText: '驳回',
                            cancelText: '取消',
                            onOk: () => onDismissOne(r.id),
                          })
                        }
                      >
                        驳回
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <Table<ReporterDetail>
                rowKey="id"
                size="small"
                dataSource={reports}
                pagination={false}
                columns={[
                  {
                    title: '举报人',
                    render: (_: unknown, r) => (
                      <Space direction="vertical" size={0}>
                        <span>{r.reporter_display}</span>
                        {r.reporter_username ? (
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 11 }}
                          >
                            @{r.reporter_username}
                          </Typography.Text>
                        ) : null}
                      </Space>
                    ),
                  },
                  {
                    title: '原因',
                    width: 110,
                    render: (_: unknown, r) => (
                      <Tag color={r.reason === 'vent' ? 'default' : 'volcano'}>
                        {translate(REASON_LABELS, r.reason)}
                      </Tag>
                    ),
                  },
                  {
                    title: '时间',
                    width: 130,
                    render: (_: unknown, r) =>
                      formatCST(r.created_at, 'MM-DD HH:mm'),
                  },
                  {
                    title: '状态',
                    width: 90,
                    render: (_: unknown, r) => (
                      <Tag color={reportStatusColor(r.status)}>
                        {translate(REPORT_STATUS_LABELS, r.status)}
                      </Tag>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </Space>
      ) : (
        <Alert type="error" message="加载失败" />
      )}
    </Drawer>
  )
}
